import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Filter,
    Download,
    Users,
    TrendingUp,
    DollarSign,
    Award,
    CheckCircle,
    Clock,
    AlertCircle
} from 'lucide-react';

export default function CertificationManagement() {
    const navigate = useNavigate();
    const [eligibleStudents, setEligibleStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [generating, setGenerating] = useState(null);

    useEffect(() => {
        fetchEligibleStudents();
    }, []);

    const fetchEligibleStudents = async () => {
        try {
            setLoading(true);
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
            const token = localStorage.getItem('token');

            const response = await fetch(
                `${API_BASE_URL}/tracking/certificates/pending`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) throw new Error('Failed to fetch eligible students');

            const data = await response.json();
            setEligibleStudents(data.students || []);
        } catch (error) {
            console.error('Error fetching eligible students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateCertificate = async (student) => {
        try {
            setGenerating(student.id);
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
            const token = localStorage.getItem('token');

            const response = await fetch(
                `${API_BASE_URL}/tracking/certificates/generate`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        studentId: student.id,
                        courseId: student.courseId,
                        grade: student.overallGrade
                    })
                }
            );

            if (!response.ok) throw new Error('Failed to generate certificate');

            const data = await response.json();
            alert(`Certificate generated successfully!\nCertificate No: ${data.certificate.certificateNumber}\nVerification Code: ${data.certificate.verificationCode}`);

            // Refresh the list
            fetchEligibleStudents();
        } catch (error) {
            console.error('Error generating certificate:', error);
            alert('Failed to generate certificate');
        } finally {
            setGenerating(null);
        }
    };

    const filteredStudents = eligibleStudents.filter(student =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.admissionNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl p-6 text-white mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold flex items-center">
                                <Award className="mr-3 h-8 w-8" />
                                Certification Management
                            </h1>
                            <p className="mt-2 text-purple-100">
                                Generate and manage student certificates
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold">{eligibleStudents.length}</div>
                            <div className="text-purple-100">Eligible Students</div>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search students..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>

                        <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            <Download className="h-4 w-4 mr-2" />
                            Export List
                        </button>
                    </div>
                </div>

                {/* Eligible Students List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">
                            Students Eligible for Certification ({filteredStudents.length})
                        </h2>

                        {filteredStudents.length === 0 ? (
                            <div className="text-center py-12">
                                <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No eligible students</h3>
                                <p className="text-gray-500">
                                    Students who have completed their courses will appear here
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campus</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Graduation Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredStudents.map((student) => (
                                            <tr key={student.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{student.name}</div>
                                                    <div className="text-sm text-gray-500">{student.admissionNumber}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {student.courseName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {student.campusName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                                        {student.overallGrade || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                                            <div
                                                                className="bg-green-600 h-2 rounded-full"
                                                                style={{ width: `${student.progress}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-sm text-gray-900">{student.progress}%</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {student.graduationDate
                                                        ? new Date(student.graduationDate).toLocaleDateString()
                                                        : 'Not set'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleGenerateCertificate(student)}
                                                        disabled={generating === student.id}
                                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                    >
                                                        {generating === student.id ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                                Generating...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Award className="h-4 w-4 mr-2" />
                                                                Generate
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
